import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';

const EditOrderModal = ({ order, isOpen, onClose, onSave }) => {
  const [backlinks, setBacklinks] = useState(order.backlinks_data || []);
  const [saving, setSaving] = useState(false);

  const addBacklink = () => {
    setBacklinks([
      ...backlinks,
      {
        url: '',
        status: 'active',
        published_date: new Date().toISOString()
      }
    ]);
  };

  const updateBacklink = (index, field, value) => {
    const updated = [...backlinks];
    updated[index][field] = value;
    setBacklinks(updated);
  };

  const removeBacklink = (index) => {
    setBacklinks(backlinks.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/orders/${order.id}/backlinks`,
        { backlinks_data: backlinks },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Backlinks updated successfully');
      onSave();
    } catch (error) {
      toast.error('Failed to update backlinks');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Playfair Display' }}>
            Order Details & Backlinks
          </DialogTitle>
          <DialogDescription>
            Order #{order.id.substring(0, 8)} - {order.plan_name} Plan ({order.backlinks} publications)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Order Details Section */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-lg mb-3">Order Requirements</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-gray-600">Website URL</Label>
                <p className="text-sm font-medium break-all">{order.order_details?.website_url || 'N/A'}</p>
              </div>
              
              <div>
                <Label className="text-xs text-gray-600">Keywords</Label>
                <div className="flex flex-wrap gap-1">
                  {order.order_details?.keywords?.map((keyword, idx) => (
                    <span key={idx} className="text-xs bg-cyan-100 text-cyan-800 px-2 py-1 rounded">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {order.order_details?.image_url && (
              <div>
                <Label className="text-xs text-gray-600">Image URL</Label>
                <p className="text-sm font-medium break-all">{order.order_details.image_url}</p>
              </div>
            )}

            {order.order_details?.youtube_url && (
              <div>
                <Label className="text-xs text-gray-600">YouTube URL</Label>
                <p className="text-sm font-medium break-all">{order.order_details.youtube_url}</p>
              </div>
            )}

            {order.order_details?.article_content && (
              <div>
                <Label className="text-xs text-gray-600">Article Content</Label>
                <p className="text-sm bg-white p-3 rounded border max-h-32 overflow-y-auto">
                  {order.order_details.article_content}
                </p>
              </div>
            )}
          </div>

          {/* Backlinks Section */}
          <div>
            <h3 className="font-semibold text-lg mb-3">Backlinks Management</h3>
          </div>
          {backlinks.map((backlink, index) => (
            <div key={index} className="border rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold">Backlink {index + 1}</h4>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeBacklink(index)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>

              <div>
                <Label>Full URL</Label>
                <Input
                  value={backlink.url}
                  onChange={(e) => updateBacklink(index, 'url', e.target.value)}
                  placeholder="https://example.com/press-release"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Status</Label>
                  <select
                    value={backlink.status}
                    onChange={(e) => updateBacklink(index, 'status', e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="removed">Removed</option>
                  </select>
                </div>

                <div>
                  <Label>Published Date</Label>
                  <Input
                    type="date"
                    value={backlink.published_date?.split('T')[0] || ''}
                    onChange={(e) => updateBacklink(index, 'published_date', new Date(e.target.value).toISOString())}
                  />
                </div>
              </div>
            </div>
          ))}

          <Button
            onClick={addBacklink}
            variant="outline"
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Backlink
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            style={{ backgroundColor: '#4AC5DB' }}
            className="hover:opacity-90"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditOrderModal;